import React, { useState, useEffect, useCallback, useRef } from 'react';

import QuickLinks from './QuickLinks/QuickLinks';
import SidebarHelp from './Sidebar/SidebarHelp';
import { useAssessment } from '../hooks/useAssessment';
import { resolveAuthorisedActiveModule, useAppState } from '../contexts/AppStateContext';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { questions, MATURITY_LEVELS } from '../data/questions';
import { useCompliance } from '../hooks/useCompliance';
import { ControlChart } from './EssentialEight/Charts';
import E8Summary from './EssentialEight/E8Summary';
import ControlIndicator from './EssentialEight/ControlIndicator';
import { getCompliancePercentage } from '../utils/complianceCalculator';
import AnswerLegend from './Legends/AnswerLegend';
import ControlSection from './ControlSection';
import { exportAssessment, importAssessment, mergeAssessment } from '../utils/assessmentIO';
import AssessmentManager from './AssessmentManager';
import { TrashIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import VulnerabilityChart from './VulnerabilityChart';
import { BellIcon } from '@heroicons/react/24/outline';
import UpdatesPopover from './Updates/UpdatesPopover';
import ReportsContent from './Reports/ReportsContent';
import { ClipboardDocumentListIcon, ShieldExclamationIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import CrownJewelAssetManager from './CrownJewels/CrownJewelAssetManager';
import QuickRiskAssessment from './CrownJewels/QuickRiskAssessment';
import AIAnalysisHelper from './AIAnalysisHelper';
import QuestionsStatus from './QuestionsStatus';
import Sidebar from './Sidebar/Sidebar';
import E8AssessmentPanel from './E8AssessmentPanel';
import E8LightPanel from './E8LightAssessment/E8LightPanel';
import E8ReferenceTool from './E8Reference/E8ReferenceTool';
import SettingsRouter from './Settings/SettingsRouter';
import { APP_VERSION } from '../constants/appConfig';
import { useSidebar } from '../contexts/SidebarContext';
import { getDeveloperMode, getInternalMode } from '../utils/developerMode';
import SecurityServicesMatrix from './SecurityServices/SecurityServicesMatrix';
import SecurityServicesAdmin from './SecurityServices/SecurityServicesAdmin';
import UserManagement from './Admin/UserManagement';
import TaskDashboard from './Tasks/TaskDashboard';
import { useAuth } from '../contexts/AuthContext';

const EssentialEightAssessment = () => {
  // Initialize fileInputRef at the very top of the component
  const fileInputRef = useRef(null);

  const {
    activeTab,
    setActiveTab,
    currentQuestions,
    setCurrentQuestions,
    answers,
    setAnswers,
    assessmentName,
    setAssessmentName,
    maturityLevel,
    setMaturityLevel,
    totalQuestions,
    compliances,
    setCompliances,
    savedAssessments,
    setSavedAssessments,
    handleAnswerChange,
    getQuestionsForML,
    deleteAssessment,
    assessmentDate,
    setAssessmentDate,
    handleDateChange,
    summaryRecommendations,
    setSummaryRecommendations,
    updateSummaryRecommendations,
    autoSave,
    hasManualSave,
    saveAssessment: saveAssessmentFromHook, // Use the unified save from hook
    loadAssessment: loadAssessmentFromHook, // Use the unified load from hook
    createNewAssessment: createNewAssessmentFromHook, // Use the unified create from hook
  } = useAssessment();

  const [recommendations, setRecommendations] = useState({});
  const [isVisible, setIsVisible] = useState(true);
  const { calculateMaturityLevelCompliance, setAnswersForCalculation } = useCompliance(getQuestionsForML);
  const [activeCategory, setActiveCategory] = useState('');
  const [tabs] = useState([
    { 
      number: 1, 
      name: "Patch Applications",
      description: "Patches/mitigates computers (workstations and servers) with 'extreme risk' vulnerabilities within 48 hours.",
    },
    { 
      number: 2, 
      name: "Patch Operating Systems",
      description: "Patches/mitigates computers with 'extreme risk' vulnerabilities within 48 hours. Uses latest operating system versions.",
    },
    { 
      number: 3, 
      name: "Multi-factor Authentication",
      description: "Uses multi-factor authentication for VPNs, RDP, SSH and other remote access, and for all users when they perform a privileged action or access important data repositories.",
    },
    { 
      number: 4, 
      name: "Restrict Administrative Privileges",
      description: "Restricts administrative privileges to operating systems and applications based on user duties. Regularly revalidates the need for privileges.",
    },
    { 
      number: 5, 
      name: "Application Control",
      description: "Prevents execution of unapproved/malicious programs including .exe, DLL, scripts, and installers.",
    },
    { 
      number: 6, 
      name: "Configure Microsoft Office macro settings",
      description: "Blocks macros from the internet, and only allows vetted macros either in 'trusted locations' with limited write access or digitally signed with a trusted certificate.",
    },
    { 
      number: 7, 
      name: "User Application Hardening",
      description: "Configures web browsers to block ads and Java on the internet. Disables unneeded features in Microsoft Office, web browsers and PDF viewers.",
    },
    { 
      name: "Regular Backups", 
      number: "8", 
      description: "Regular backups are made of all data repositories." 
    }
  ]);

  // Use global app state for module navigation
  const { 
    activeModule, 
    setActiveModule, 
    selectedReport, 
    setSelectedReport,
    activeCJSection,
    setActiveCJSection,
    getCompleteAppState
  } = useAppState();
  const { userModules } = useModuleAccess();
  const authorisedActiveModule = resolveAuthorisedActiveModule(activeModule, userModules);

  useEffect(() => {
    if (authorisedActiveModule && authorisedActiveModule !== activeModule) setActiveModule(authorisedActiveModule);
  }, [activeModule, authorisedActiveModule, setActiveModule]);

  // Debug: Log app state on component mount and when activeModule changes
  useEffect(() => {
    console.log('🌐 EssentialEightAssessment: Complete app state:', getCompleteAppState());
    console.log('🌐 EssentialEightAssessment: Active module:', activeModule);
  }, [activeModule, getCompleteAppState]);

  const crownJewelsMenu = [
    {
      id: 1,
      name: "Crown Jewels",
      description: "Identify and document critical assets"
    },
    {
      id: 2,
      name: "Vulnerability Management",
      description: "Track and manage vulnerabilities across crown jewel assets"
    },
    {
      id: 3,
      name: "Quick Risk Assessment",
      description: "Rapidly assess security risks for third-party applications"
    }
  ];

  const handleRecommendationsChange = (controlName, value) => {
    setRecommendations(prev => ({
      ...prev,
      [controlName]: value
    }));
  };

  // Calculate compliance for a specific control and maturity level
  const calculateControlCompliance = useCallback((controlName, ml) => {
    return calculateMaturityLevelCompliance(controlName, ml, answers);
  }, [calculateMaturityLevelCompliance, answers]);

// Create a custom QuestionHelp component inline or in a separate file
const QuestionHelp = () => (
  <svg 
    className="w-5 h-5" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <circle cx="12" cy="17" r="1" />
  </svg>
);

  // Set up initial active category
  useEffect(() => {
    if (tabs && tabs.length > 0) {
      setActiveCategory(tabs[0].name);
    }
  }, [tabs]);

  // Update questions when tab or maturity level change
  useEffect(() => {
    console.log('Effect triggered with:', { maturityLevel, activeTab });
    if (maturityLevel && activeTab < tabs.length) {
      const activeCategory = tabs[activeTab].name;
      console.log('Active category:', activeCategory);
      let cumulativeQuestions = [];
      if (maturityLevel === 'ML1') {
        cumulativeQuestions = getQuestionsForML(activeCategory, 'ML1');
      } else if (maturityLevel === 'ML2') {
        cumulativeQuestions = [
          ...getQuestionsForML(activeCategory, 'ML1'),
          ...getQuestionsForML(activeCategory, 'ML2')
        ];
      } else if (maturityLevel === 'ML3') {
        cumulativeQuestions = [
          ...getQuestionsForML(activeCategory, 'ML1'),
          ...getQuestionsForML(activeCategory, 'ML2'),
          ...getQuestionsForML(activeCategory, 'ML3')
        ];
      }
      console.log('Questions to set:', cumulativeQuestions);
      setCurrentQuestions(cumulativeQuestions);
    }
  }, [activeTab, maturityLevel, tabs, getQuestionsForML]);

   // Create new assessment without confirmation dialog (confirmation handled by E8AssessmentPanel)
   const createNewAssessment = (initialMaturityLevel = null) => {
    console.log('🆕 EssentialEightAssessment: Creating new assessment, redirecting to unified function...');
    createNewAssessmentFromHook();
    
    // Set the maturity level after creating new assessment
    if (initialMaturityLevel) {
      setTimeout(() => {
        setMaturityLevel(initialMaturityLevel);
        console.log('🆕 EssentialEightAssessment: Set maturity level:', initialMaturityLevel);
      }, 50);
    }
  };

  // Save assessment using unified storage system
  const saveAssessment = (assessmentNameToSave = null, onSuccess = null, onError = null) => {
    console.log('💾 EssentialEightAssessment: Save requested, redirecting to unified function...');
    saveAssessmentFromHook(assessmentNameToSave, onSuccess, onError);
  };

   // Load assessment using unified storage system
   const loadAssessment = (assessment) => {
    console.log('📂 EssentialEightAssessment: Load requested, redirecting to unified function...');
    loadAssessmentFromHook(assessment);
  };

    // Helper function to update compliances
    const updateCompliances = (currentAnswers, currentTab) => {
      const newCompliances = {};
      Object.keys(MATURITY_LEVELS).forEach(ml => {
        newCompliances[ml] = calculateMaturityLevelCompliance(
          tabs[currentTab].name,
          ml,
          currentAnswers
        );
      });
      setCompliances(newCompliances);
    };

     // Update questions when tab or maturity level changes
  useEffect(() => {
    if (maturityLevel && activeTab < tabs.length) {
      const activeCategory = tabs[activeTab].name;
      let cumulativeQuestions = [];
      
      if (maturityLevel === 'ML1') {
        cumulativeQuestions = getQuestionsForML(activeCategory, 'ML1');
      } else if (maturityLevel === 'ML2') {
        cumulativeQuestions = [
          ...getQuestionsForML(activeCategory, 'ML1'),
          ...getQuestionsForML(activeCategory, 'ML2')
        ];
      } else if (maturityLevel === 'ML3') {
        cumulativeQuestions = [
          ...getQuestionsForML(activeCategory, 'ML1'),
          ...getQuestionsForML(activeCategory, 'ML2'),
          ...getQuestionsForML(activeCategory, 'ML3')
        ];
      }
      
      setCurrentQuestions(cumulativeQuestions);
      updateCompliances(answers, activeTab);
    }
  }, [activeTab, maturityLevel, tabs, getQuestionsForML, answers]);

    // Render control chart with memoization
    const renderControlChart = useCallback((ml) => {
      let compliancePercentage = 0;
      const currentControl = tabs[activeTab]?.name;
      const isPatchOS = currentControl === 'Patch Operating Systems';
      
      // For Patch Operating Systems:
      // - ML1: Show actual ML1 compliance
      // - ML2: Auto-complete when ML1 is complete
      // - ML3: Show actual ML3 compliance when ML1 is complete
      if (isPatchOS) {
        const ml1Compliance = calculateControlCompliance(currentControl, 'ML1');
        
        if (ml === 'ML1') {
          compliancePercentage = ml1Compliance;
        } else if (ml === 'ML2') {
          compliancePercentage = ml1Compliance; // ML2 mirrors ML1 for Patch OS
        } else if (ml === 'ML3') {
          // For ML3, directly check if all ML3 questions are effective
          const ml3Questions = getQuestionsForML(currentControl, 'ML3');
          const allML3Effective = ml3Questions.every(q => answers[q.id]?.value === 'effective');
          compliancePercentage = allML3Effective ? 100 : 0;
        }
      } else {
        // Original logic for other controls
        if (ml === 'ML1') {
          compliancePercentage = calculateControlCompliance(currentControl, 'ML1');
        } else if (ml === 'ML2') {
          const ml1Compliance = calculateControlCompliance(currentControl, 'ML1');
          compliancePercentage = ml1Compliance === 100 ? calculateControlCompliance(currentControl, 'ML2') : 0;
        } else if (ml === 'ML3') {
          const ml1Compliance = calculateControlCompliance(currentControl, 'ML1');
          const ml2Compliance = calculateControlCompliance(currentControl, 'ML2');
          compliancePercentage = (ml1Compliance === 100 && ml2Compliance === 100) ? 
            calculateControlCompliance(currentControl, 'ML3') : 0;
        }
      }
  
      return <ControlChart key={ml} ml={ml} compliancePercentage={compliancePercentage} />;
    }, [activeTab, tabs, calculateControlCompliance, getQuestionsForML, answers]);

  const getAnsweredCount = useCallback((controlName) => {
    let count = 0;
    const levels = ['ML1', 'ML2', 'ML3'];
    const selectedLevelIndex = levels.indexOf(maturityLevel);

    for (let i = 0; i <= selectedLevelIndex; i++) {
      const ml = levels[i];
      const questionsForML = getQuestionsForML(controlName, ml);
      count += questionsForML.filter(q => answers[q.id] && answers[q.id].value !== '').length;
    }

    return count;
  }, [maturityLevel, getQuestionsForML, answers]);

  const getTotalQuestionsCount = useCallback((controlName) => {
    let count = 0;
    const levels = ['ML1', 'ML2', 'ML3'];
    const selectedLevelIndex = levels.indexOf(maturityLevel);

    for (let i = 0; i <= selectedLevelIndex; i++) {
      const ml = levels[i];
      count += getQuestionsForML(controlName, ml).length;
    }

    return count;
  }, [maturityLevel, getQuestionsForML]);

  const handlePrevious = () => {
    setActiveTab(prev => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setActiveTab(prev => Math.min(prev + 1, tabs.length - 1));
  };

  const getTotalControlsCount = useCallback(() => {
    if (!maturityLevel) return 0;
    return tabs.reduce((total, tab) => {
      let count = 0;
      if (maturityLevel === 'ML1') {
        count = (questions[tab.name]?.ML1 || []).length;
      } else if (maturityLevel === 'ML2') {
        count = (questions[tab.name]?.ML1 || []).length + 
                (questions[tab.name]?.ML2 || []).length;
      } else if (maturityLevel === 'ML3') {
        count = (questions[tab.name]?.ML1 || []).length + 
                (questions[tab.name]?.ML2 || []).length + 
                (questions[tab.name]?.ML3 || []).length;
      }
      return total + count;
    }, 0);
  }, [maturityLevel, tabs]);

  // Calculate progress percentage
  const getProgressPercentage = useCallback(() => {
    if (!tabs[activeTab]) return 0;
    const answeredCount = getAnsweredCount(tabs[activeTab].name);
    const totalCount = getTotalQuestionsCount(tabs[activeTab].name);
    return totalCount > 0 ? (answeredCount / totalCount) * 100 : 0;
  }, [activeTab, tabs, getAnsweredCount, getTotalQuestionsCount]);

  const controlDescriptions = {
    'Patch Applications': {
      ML1: 'Patches/mitigates extreme risk vulnerabilities within 48 hours.',
      ML2: 'Patches/mitigates high or critical risk vulnerabilities within two weeks.',
      ML3: 'Patches/mitigates medium risk vulnerabilities within one month.'
    },
    'Patch Operating Systems': {
      ML1: 'Patches critical vulnerabilities in 48 hours, others in 2-4 weeks.',
      ML2: 'Maintains patching standards established in ML1.',
      ML3: 'Extends patching to drivers and firmware, uses latest OS versions.'
    },
    'Multi-factor Authentication': {
      ML1: 'Implements MFA for remote access and important data/actions.',
      ML2: 'Extends MFA to all users, uses phishing-resistant methods.',
      ML3: 'Implements phishing-resistant MFA for all users and data repositories.'
    },
    'Restrict Administrative Privileges': {
      ML1: 'Validates privilege requests, uses separate admin accounts.',
      ML2: 'Implements time-based access, auditing, and jump servers.',
      ML3: 'Uses Just-In-Time administration and secure admin workstations.'
    },
      'Application Control': {
      ML1: 'Restricts execution on workstations to an approved set.',
      ML2: 'Extends control to internet-facing servers and all disk locations.',
      ML3: 'Includes driver execution restriction and vulnerable driver blocklist.'
    },
    'Configure Microsoft Office macro settings': {
      ML1: 'Microsoft Office macros are disabled for users that do not have a demonstrated business requirement.',
      ML2: 'Microsoft Office macros in files originating from the internet are blocked.',
      ML3: 'Microsoft Office macro antivirus scanning is enabled.'
    },
    'User Application Hardening': {
      ML1: 'Internet Explorer 11 is disabled or removed.',
      ML2: 'Web browsers do not process Java from the internet.',
      ML3: 'Web browsers do not process Java from the internet.'
    },
    'Regular Backups': {
      ML1: 'Performs regular backups, restricts access, tests annually.',
      ML2: 'Prevents modification/deletion by unprivileged accounts.',
      ML3: 'Further restricts backup access and modification rights.'
    }
  };

  const getControlDescription = useCallback((controlName) => {
    if (!controlDescriptions[controlName]) return "Description not available.";
    
    let description = '';
    if (maturityLevel === 'ML1' || maturityLevel === 'ML2' || maturityLevel === 'ML3') {
      description += `ML1: ${controlDescriptions[controlName].ML1}\n`;
    }
    if (maturityLevel === 'ML2' || maturityLevel === 'ML3') {
      description += `ML2: ${controlDescriptions[controlName].ML2}\n`;
    }
    if (maturityLevel === 'ML3') {
      description += `ML3: ${controlDescriptions[controlName].ML3}`;
    }
    return description.trim();
  }, [maturityLevel]);

  // Log the current state
  console.log('Rendering with:', { 
    currentQuestions, 
    maturityLevel,
    activeTab,
    tabName: tabs[activeTab]?.name
  });

  const [showUpdates, setShowUpdates] = useState(false);
  const [showUpdateBanner, setShowUpdateBanner] = useState(() => {
    const lastSeenVersion = localStorage.getItem('lastSeenUpdateVersion');
    return lastSeenVersion !== APP_VERSION;
  });

  const dismissUpdateBanner = () => {
    setShowUpdateBanner(false);
    localStorage.setItem('lastSeenUpdateVersion', APP_VERSION);
  };

  const handleReportSelection = (reportId) => {
    const report = reportsMenu.find(r => r.id === reportId);
    setSelectedReport(report);
  };

  // Update the date formatting function
  const formatDate = (date) => {
    if (!date) return new Date().toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    return new Date(date).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Initialize collapsedSections with all sections collapsed
  const [collapsedSections, setCollapsedSections] = useState(() => 
    tabs.reduce((acc, _, index) => ({...acc, [index]: true}), {})
  );

  // activeCJSection is now managed by global app state

  const [newClientName, setNewClientName] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [showAssetForm, setShowAssetForm] = useState(false);
  const [crownJewelAssets, setCrownJewelAssets] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editAssetId, setEditAssetId] = useState(null);

  const handleAddClient = () => {
    if (newClientName.trim()) {
      const newClient = { id: Date.now().toString(), name: newClientName };
      const updatedClients = [...clients, newClient];
      setClients(updatedClients);
      setNewClientName('');
      setSelectedClient(newClient.id);
      
    }
  };

  const handleAddAsset = () => {
    setIsEditMode(false);
    setEditAssetId(null);
    setShowAssetForm(true);
  };

  const handleSaveAsset = (assetData) => {
    let updatedAssets;
    
    if (isEditMode && editAssetId) {
      // Update existing asset
      updatedAssets = crownJewelAssets.map(asset => 
        asset.id === editAssetId ? { ...assetData, id: editAssetId, clientId: selectedClient } : asset
      );
    } else {
      // Add new asset
      updatedAssets = [...crownJewelAssets, {
        ...assetData,
        id: Date.now().toString(),
        clientId: selectedClient
      }];
    }
    
    setCrownJewelAssets(updatedAssets);
    
    // Reset form
    setShowAssetForm(false);
    setIsEditMode(false);
    setEditAssetId(null);
  };

  const handleCancelAssetForm = () => {
    setShowAssetForm(false);
    setIsEditMode(false);
    setEditAssetId(null);
  };

  const handleEditAsset = (assetId) => {
    setIsEditMode(true);
    setEditAssetId(assetId);
    setShowAssetForm(true);
  };

  const handleDeleteAsset = (assetId) => {
    const updatedAssets = crownJewelAssets.filter(asset => asset.id !== assetId);
    setCrownJewelAssets(updatedAssets);
  };

  useEffect(() => {
    // When module changes to Crown Jewels (CJ), set the active section to crownJewels
    if (authorisedActiveModule === 'CJ') {
      setActiveCJSection('crownJewels');
    }
  }, [authorisedActiveModule, setActiveCJSection]);

  // Set the initial activeModule to 'E8' to ensure the component loads properly
  useEffect(() => {
    // Ensure fileInputRef is defined and initialized
    console.log('fileInputRef initialized:', fileInputRef?.current);
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target.result);
          
          // Check if the imported data has the expected format
          if (!importedData.assessment || !importedData.version) {
            throw new Error('Invalid assessment file format');
          }
          
          // Extract the assessment data and load it
          const assessmentData = importedData.assessment;
          
          // Update the state with the new assessment
          loadAssessment(assessmentData);
          
        } catch (error) {
          console.error('Error importing assessment:', error);
          alert('Error importing assessment. Please check the file format.');
        }
      };
      reader.readAsText(file);
    }
  };

  // Use sidebar context
  const { isCollapsed } = useSidebar();

  // Use auth context
  const { isAdmin } = useAuth();

  // Check if developer mode is enabled
  const isDeveloperModeEnabled = getDeveloperMode();
  
  // Check if internal mode is enabled
  const isInternalModeEnabled = getInternalMode();

  // Render based on active module
  let content;
  
  switch (authorisedActiveModule) {
    case 'E8':
      content = (
        <E8AssessmentPanel
          assessmentName={assessmentName}
          setAssessmentName={setAssessmentName}
          maturityLevel={maturityLevel}
          setMaturityLevel={setMaturityLevel}
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          currentQuestions={currentQuestions}
          answers={answers}
          handleAnswerChange={handleAnswerChange}
          getQuestionsForML={getQuestionsForML}
          summaryRecommendations={recommendations}
          calculateMaturityLevelCompliance={calculateMaturityLevelCompliance}
          saveAssessment={saveAssessment}
          loadAssessment={loadAssessment}
          createNewAssessment={createNewAssessment}
          savedAssessments={savedAssessments}
          setSavedAssessments={setSavedAssessments}
          assessmentDate={assessmentDate}
          handleDateChange={handleDateChange}
          fileInputRef={fileInputRef}
          deleteAssessment={deleteAssessment}
          handleRecommendationsChange={handleRecommendationsChange}
          hideOverallProgress={true}
          autoSave={autoSave}
          hasManualSave={hasManualSave}
        />
      );
      break;
    case 'E8Light':
      content = (
        <E8LightPanel
          assessmentName={assessmentName}
          setAssessmentName={setAssessmentName}
          maturityLevel={maturityLevel}
          setMaturityLevel={setMaturityLevel}
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          answers={answers}
          handleAnswerChange={handleAnswerChange}
          summaryRecommendations={recommendations}
          calculateMaturityLevelCompliance={calculateMaturityLevelCompliance}
          saveAssessment={saveAssessment}
          loadAssessment={loadAssessment}
          createNewAssessment={createNewAssessment}
          savedAssessments={savedAssessments}
          setSavedAssessments={setSavedAssessments}
          assessmentDate={assessmentDate}
          handleDateChange={handleDateChange}
          fileInputRef={fileInputRef}
          deleteAssessment={deleteAssessment}
        />
      );
      break;
    case 'Reports':
      content = (
        <ReportsContent 
          selectedReport={selectedReport} 
        />
      );
      break;
    case 'CJ':
      // Crown Jewels module - now generally available (v3.6.0)
      content = (
        <CrownJewelAssetManager
          newClientName={newClientName}
          setNewClientName={setNewClientName}
          clients={clients}
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          handleAddClient={handleAddClient}
          showAssetForm={showAssetForm}
          setShowAssetForm={setShowAssetForm}
          isEditMode={isEditMode}
          editAssetId={editAssetId}
          crownJewelAssets={crownJewelAssets}
          handleAddAsset={handleAddAsset}
          handleSaveAsset={handleSaveAsset}
          handleCancelAssetForm={handleCancelAssetForm}
          handleEditAsset={handleEditAsset}
          handleDeleteAsset={handleDeleteAsset}
        />
      );
      break;
    case 'QRA':
      content = (
        <div className="flex-1 overflow-y-auto">
          <div className="sticky top-0 bg-white p-6 shadow-sm z-10">
            <h1 className="text-3xl font-bold text-gray-800">Quick Risk Assessment</h1>
          </div>
          <QuickRiskAssessment />
        </div>
      );
      break;
    case 'Vulnerability':
      content = (
        <div className="flex-1 overflow-y-auto">
          <div className="sticky top-0 bg-white p-6 shadow-sm z-10">
            <h1 className="text-3xl font-bold text-gray-800">Vulnerability Management</h1>
          </div>
          <div className="p-6">
            <VulnerabilityChart />
          </div>
        </div>
      );
      break;
    case 'Services':
      // Route protection: Only allow access in internal mode
      if (!isInternalModeEnabled) {
        content = (
          <div className="flex-1 overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 shadow-sm z-10">
              <h1 className="text-3xl font-bold text-gray-800">Module Unavailable</h1>
            </div>
            <div className="p-6">
              <div className="max-w-md mx-auto mt-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">This module is currently unavailable</h3>
                <p className="text-gray-500">
                  The Security Services Matrix is only available in internal mode. Please contact your administrator for access.
                </p>
              </div>
            </div>
          </div>
        );
      } else {
        content = <SecurityServicesMatrix />;
      }
      break;
    case 'ServicesAdmin':
      // Route protection: Only allow access in internal mode
      if (!isInternalModeEnabled) {
        content = (
          <div className="flex-1 overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 shadow-sm z-10">
              <h1 className="text-3xl font-bold text-gray-800">Module Unavailable</h1>
            </div>
            <div className="p-6">
              <div className="max-w-md mx-auto mt-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">This module is currently unavailable</h3>
                <p className="text-gray-500">
                  The Services Matrix Admin is only available in internal mode. Please contact your administrator for access.
                </p>
              </div>
            </div>
          </div>
        );
      } else {
        content = <SecurityServicesAdmin />;
      }
      break;
    case 'E8Reference':
      // Route protection: Only allow access in developer mode
      if (!isDeveloperModeEnabled) {
        content = (
          <div className="flex-1 overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 shadow-sm z-10">
              <h1 className="text-3xl font-bold text-gray-800">Module Unavailable</h1>
            </div>
            <div className="p-6">
              <div className="max-w-md mx-auto mt-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">This module is currently unavailable</h3>
                <p className="text-gray-500">
                  The E8 Reference Tool is under development and not yet available for public use.
                </p>
              </div>
            </div>
          </div>
        );
      } else {
        content = (
          <E8ReferenceTool />
        );
      }
      break;
    case 'Settings':
      // Route protection: Only allow access in developer mode
      if (!isDeveloperModeEnabled) {
        content = (
          <div className="flex-1 overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 shadow-sm z-10">
              <h1 className="text-3xl font-bold text-gray-800">Module Unavailable</h1>
            </div>
            <div className="p-6">
              <div className="max-w-md mx-auto mt-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">This module is currently unavailable</h3>
                <p className="text-gray-500">
                  Settings are under development and not yet available for public use.
                </p>
              </div>
            </div>
          </div>
        );
      } else {
        content = (
          <SettingsRouter />
        );
      }
      break;
    case 'UserManagement':
      // Route protection: Only allow access for admin users
      if (!isAdmin) {
        content = (
          <div className="flex-1 overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 shadow-sm z-10">
              <h1 className="text-3xl font-bold text-gray-800">Access Denied</h1>
            </div>
            <div className="p-6">
              <div className="max-w-md mx-auto mt-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Administrator Access Required</h3>
                <p className="text-gray-500">
                  This module is only available to administrators. Please contact your administrator for access.
                </p>
              </div>
            </div>
          </div>
        );
      } else {
        content = <UserManagement />;
      }
      break;
    case 'E8Tasks':
      content = <TaskDashboard />;
      break;
    default:
      content = <div>Select a module</div>;
  }

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden relative">
      {/* Left Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div 
        className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ease-in-out ${
          isCollapsed ? 'ml-16' : 'ml-64'
        }`}
        style={{ willChange: 'margin-left' }}
      >
        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          {content}
        </div>
      </div>

      {/* Hidden file input for importing assessments */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".json"
        onChange={handleFileChange}
      />
    </div>
  );
};

export default EssentialEightAssessment;
